import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { attentionItems, type AttentionItem, type AttentionTone } from '@/lib/attention'
import { useAlerts, useGoogleStatus, useSyncRuns } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { errorText } from './ErrorState'
import { StatusDot } from './StatusPill'
import { When } from './When'

/** The band under every page header that lists what is broken right now: the
 *  sync failing, AdMob failing, the sync falling behind, alerts not yet read.
 *  When nothing is wrong it is not drawn at all.
 *
 *  Fed by the last five sync runs, the Google status and the alerts — polled
 *  every 30 seconds through the shared query cache. Each item goes away by
 *  itself once the thing works (an alert, once it is read). */
export function AttentionStrip({ omit, className }: { omit?: string[]; className?: string }) {
  const items = useAttentionItems()
  const shown = useMemo(() => (omit?.length ? items.filter((i) => !omit.includes(i.key)) : items), [items, omit])
  return <AttentionList items={shown} className={className} />
}

/** The strip's items, for a page that shows them its own way (오늘 folds
 *  them into its "확인할 것" list instead of drawing the strip) and for the
 *  오늘 badge. */
export function useAttentionItems(): AttentionItem[] {
  const sync = useSyncRuns()
  const status = useGoogleStatus()
  const alerts = useAlerts()
  return useMemo(() => {
    const list = attentionItems({ runs: sync.runs, status: status.data, alerts: alerts.data })
    if (sync.error) {
      list.unshift({
        key: 'sync-runs-error',
        tone: 'failed',
        label: '동기화 기록을 읽지 못했다',
        count: 0,
        to: '/jobs?job=sync',
        details: [{ where: 'GET /api/admin/jobs/runs', message: errorText(sync.error) }],
      })
    }
    return list
  }, [sync.runs, sync.error, status.data, alerts.data])
}

function worst(items: AttentionItem[]): AttentionTone {
  if (items.some((i) => i.tone === 'failed')) return 'failed'
  if (items.some((i) => i.tone === 'publishing')) return 'publishing'
  return 'neutral'
}

const BAR: Record<AttentionTone, string> = {
  failed: 'bg-status-failed',
  publishing: 'bg-status-publishing',
  neutral: 'bg-status-neutral',
}

const TITLE: Record<AttentionTone, string> = {
  failed: 'text-status-failed',
  publishing: 'text-status-publishing',
  neutral: 'text-foreground',
}

/** The strip's drawing, apart from its data — tests render this directly. */
export function AttentionList({ items, className }: { items: AttentionItem[]; className?: string }) {
  const [open, setOpen] = useState(false)
  if (items.length === 0) return null
  const tone = worst(items)
  return (
    <section
      aria-label="확인할 것"
      className={cn('relative overflow-hidden rounded-lg border border-border bg-card pl-1', className)}
    >
      <span
        aria-hidden
        className={cn('absolute inset-y-0 left-0 w-1', BAR[tone])}
      />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2">
        <h2
          className={cn(
            'order-1 flex shrink-0 items-center gap-2 text-table font-semibold',
            TITLE[tone],
          )}
        >
          <StatusDot tone={tone} />
          <span>
            확인할 것 <span className="tabular-nums">{items.length}</span>건
          </span>
        </h2>
        {/* One line on the phone, sideways if it has to: the strip is on every
            page and should not push the page down. */}
        <ul className="order-3 -mx-3 flex w-[calc(100%+1.5rem)] min-w-0 gap-1.5 overflow-x-auto px-3 [scrollbar-width:none] md:order-2 md:mx-0 md:w-auto md:flex-1 md:flex-wrap md:overflow-visible md:px-0 [&::-webkit-scrollbar]:hidden">
          {items.map((i) => (
            <li key={i.key} className="shrink-0">
              <Link
                to={i.to}
                className="inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-background px-2.5 text-meta font-medium text-foreground transition-colors hover:border-foreground/25 hover:bg-muted"
              >
                <StatusDot tone={i.tone} />
                {i.label}
                {i.count > 0 && <span className="tabular-nums text-muted-foreground">{i.count}</span>}
              </Link>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="order-2 ml-auto inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-meta font-medium text-muted-foreground hover:bg-muted hover:text-foreground md:order-3"
        >
          {open ? '접기' : '자세히'}
          <ChevronDown size={14} className={cn('transition-transform', open && 'rotate-180')} aria-hidden />
        </button>
      </div>
      {open && (
        <div className="space-y-3 border-t border-border bg-muted/30 px-3 py-3">
          {items.map((i) => (
            <div key={i.key} className="space-y-1.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-table font-semibold">
                  {i.label}
                  {i.count > 0 && <span className="ml-1 tabular-nums text-muted-foreground">{i.count}</span>}
                </span>
                <span className="flex items-baseline gap-3">
                  <Link to={i.to} className="text-meta font-medium text-accent hover:underline">
                    열기
                  </Link>
                </span>
              </div>
              <ul className="space-y-1.5">
                {i.details.map((d, n) => (
                  <li key={n} className="text-meta">
                    <div className="flex flex-wrap items-baseline gap-x-2 text-muted-foreground">
                      <span className="font-medium text-foreground">{d.where}</span>
                      {d.what && <span className="min-w-0 truncate">{d.what}</span>}
                      {d.at && <When iso={d.at} className="ml-auto shrink-0" />}
                    </div>
                    {d.message && (
                      <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-muted px-2 py-1 font-mono text-micro text-foreground">
                        {d.message}
                      </pre>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
